<!--
  The live verdict page for one promo: the shared PromoVerdictPage with the API behind
  it. Everything visible lives in components/promotions/PromoVerdictPage.vue, which the
  demo store mounts too.
-->
<template>
  <Section v-if="error" :title="$t('promos.title')" margin-top="20px">
    <div class="promo-state promo-state-bad">
      <i class="bi bi-exclamation-triangle"></i>
      <div>
        <b>{{ $t('promos.view.promoLoadFailed') }}</b>
        <p>{{ error }}</p>
        <button type="button" class="btn-primary" @click="load">{{ $t('promos.view.tryAgain') }}</button>
      </div>
    </div>
  </Section>

  <PromoVerdictPage
    v-else
    :promo="promo"
    :can-edit="isAdmin"
    @save-sales="onSaveSales"
    @save-outcome="onSaveOutcome"
  />
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PromoVerdictPage from '@/components/promotions/PromoVerdictPage.vue'
import Section from '@/components/ui/Section.vue'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useUserStore } from '@/stores/UserStore'

const route = useRoute()
const { t } = useI18n()
const store = useCompanyStore()
const userStore = useUserStore()

const promo = ref(null)
const error = ref('')
const isAdmin = computed(() => !!userStore.isAdminInCurrentCompany)

/**
 * `keep` is true when re-reading the same promo after a write: clearing it first
 * would flash the not-found state and, because the page resets its panels whenever
 * the promo's identity changes, wipe the confirmation the save had just put up.
 */
async function load({ keep = false } = {}) {
  error.value = ''
  if (!keep) promo.value = null
  const id = route.params.promoId
  try {
    const [rows, measurement] = await Promise.all([
      store.fetchPromos(), store.measurePromo(id),
    ])
    const row = rows.find((p) => String(p.PromoId) === String(id))
    if (!row) return                       // the page renders its own not-found state
    promo.value = {
      ...row,
      Data: typeof row.Data === 'string' ? JSON.parse(row.Data || '{}') : (row.Data || {}),
      measurement,
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || t('promos.view.unknownError')
  }
}

/**
 * PATCH merges salesPeriods per period on the server, because the three till figures
 * become knowable weeks apart — so sending only what the form holds is safe. The
 * measurement is then recomputed, which is the whole point of saving them.
 */
async function onSaveSales({ salesPeriods, fixedCostEur, storeMarginPct }) {
  try {
    // The set-up cost is edited in the same panel, because it is the third thing the
    // euro is made of and it is usually known only after the promo has run. A store
    // promo's average margin % rides along for the same reason, and only then.
    await store.updatePromo(route.params.promoId, {
      salesPeriods, fixedCostEur,
      ...(storeMarginPct === undefined ? {} : { storeMarginPct }),
    })
    await load({ keep: true })
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || t('promos.view.figuresFailed')
  }
}

async function onSaveOutcome(outcome) {
  try {
    await store.updatePromo(route.params.promoId, { outcome })
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || t('promos.view.answerFailed')
  }
}

onMounted(load)
watch(() => route.params.promoId, load)
</script>

<style scoped>
.promo-state {
  display: flex; gap: 14px; max-width: 680px;
  margin: 4px 0 0; padding: 18px 20px;
  background: #fdf6f5; border: 1px solid #f0d9d6; border-radius: 12px;
  color: #425461; font-size: 0.9rem; line-height: 1.55;
}
.promo-state > i { font-size: 1.2rem; color: #b3372f; }
.promo-state b { display: block; margin-bottom: 4px; color: #1d2b3a; }
.promo-state p { margin: 0 0 10px; }
.btn-primary {
  padding: 7px 15px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--stora-accent, #4F7EB3); color: #fff;
  font-size: 13px; font-weight: 600;
}
</style>
